import Vue from "vue";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { LocalNotifications } from "@capacitor/local-notifications";
import { DeviceToken_Register, DeviceToken_Unregister } from "@/apis/api";
import router from "@/router";
import store from "@/store";
import { leavesPage, waitAtMost, resolveNotificationRoute } from "@/utils/notification-nav";

let listenersBound = false;
// 平台 API 的 PushService 發推播時指定用這個頻道（有聲音、會跳橫幅），兩邊的 id 要一致；
// 改名的話，背景推播會退回系統的「其他」頻道，又變成安靜的
const PUSH_CHANNEL_ID = "default";
// 這支手機的推播 token（Firebase 發的），登出時用來解除綁定
const PUSH_TOKEN_KEY = "_pushToken";

/**
 * 原生外掛：查 App 的通知實際上有沒有開、打開系統的通知設定頁
 * （android/app/src/main/java/com/kingfong/taro/NotificationSettingsPlugin.java）。
 * 舊版 App 沒有，用之前先 Capacitor.isPluginAvailable 判斷。
 */
const NotificationSettings = registerPlugin("NotificationSettings");

function hasNotificationSettingsPlugin() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("NotificationSettings");
}

/**
 * 推播狀態，推播對話框與通知中心的設定卡共用（響應式）。
 *
 *   permission  granted / denied / prompt / prompt-with-rationale / unsupported / unknown
 *   registered  這次開啟 App 是否已把 token 註冊到後端（綁目前登入的帳號）
 */
export const pushState = Vue.observable({
  permission: "unknown",
  registered: false,
});

/**
 * 依推播 data.route 導向頁面。支援站內路由(/xxx)與外部連結(http)。
 * 冷啟動(App 剛被叫醒)時 router 可能尚未就緒，延後重試。
 */
function navigateToRoute(route, retry = 0) {
  if (!route) return;
  try {
    if (/^https?:\/\//i.test(route)) {
      window.location.href = route;
      return;
    }
    if (router && router.currentRoute && router.app) {
      if (router.currentRoute.fullPath !== route) router.push(route).catch(() => {});
    } else if (retry < 20) {
      setTimeout(() => navigateToRoute(route, retry + 1), 250);
    }
  } catch (e) {
    if (retry < 20) setTimeout(() => navigateToRoute(route, retry + 1), 250);
  }
}

/**
 * 點擊/收到推播後，若帶 notificationId 則標記已讀並刷新未讀數。
 * 回傳「已讀」請求的 promise：要整頁跳走的話得先等它送完（見 utils/notification-nav）。
 */
function syncNotificationState(data, markRead) {
  try {
    const id = data && data.notificationId ? Number(data.notificationId) : null;
    const marking = markRead && id ? store.dispatch("notifications/markRead", id) : Promise.resolve();
    store.dispatch("notifications/fetchUnreadCount");
    return marking;
  } catch (e) {
    return Promise.resolve();
  }
}

// 前景時用本地通知補跳系統通知（否則 App 開著不會顯示在通知列）
async function showForegroundNotification(n) {
  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(Date.now() % 2147483647),
          title: n.title || "通知",
          body: n.body || "",
          channelId: PUSH_CHANNEL_ID,
          smallIcon: "ic_stat_notify",
          extra: n.data || {},
        },
      ],
    });
  } catch (e) {
    console.error("[push] 前景本地通知顯示失敗", e);
  }
}

/** 是否在原生 App（Capacitor）環境 */
export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/**
 * 查詢目前通知權限狀態：'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unsupported'
 *
 * 推播外掛在 Android 12 以下一律回 granted（那些版本沒有執行期權限），
 * 使用者在系統設定把通知關掉也看不出來 —— 有原生外掛時再查一次實際狀態。
 * 結果同步寫進 pushState。
 */
export async function getPushPermissionState() {
  let state = "unsupported";
  if (Capacitor.isNativePlatform()) {
    try {
      state = (await PushNotifications.checkPermissions()).receive;
      if (state === "granted" && hasNotificationSettingsPlugin()) {
        const { enabled } = await NotificationSettings.check();
        if (!enabled) state = "denied";
      }
    } catch (e) {
      state = "unsupported";
    }
  }
  pushState.permission = state;
  return state;
}

/** 能不能直接打開系統的通知設定頁（舊版 App 不行，只能文字說明） */
export function canOpenNotificationSettings() {
  return hasNotificationSettingsPlugin();
}

/** 打開這個 App 的系統通知設定頁。回到 App 時由 watchPushPermissionOnResume 重新檢查 */
export function openNotificationSettings() {
  return NotificationSettings.open();
}

/**
 * 權限已允許、但這次開啟 App 還沒註冊 → 註冊。已註冊就不重複打後端。
 *
 * 原本只在 App 開啟時檢查一次：使用者先按「稍後再說」或拒絕、之後才到系統設定打開，
 * 那一次開啟就永遠不會註冊，後端沒有這支手機、推播發了也收不到。
 * 現在啟動、登入、回到前景都會呼叫這裡。
 *
 * 回傳是否已完成註冊。
 */
export async function ensurePushRegistered() {
  if (!Capacitor.isNativePlatform() || !store.state.signinStatus) return false;
  const state = await getPushPermissionState();
  if (state !== "granted") return false;
  if (pushState.registered) return true;
  try {
    await initPush();
    return true;
  } catch (e) {
    return false;
  }
}

let resumeBound = false;

/** 從系統設定回到 App 時重新檢查：使用者可能剛在設定裡打開（或關掉）通知 */
export function watchPushPermissionOnResume() {
  if (!Capacitor.isNativePlatform() || resumeBound) return;
  resumeBound = true;
  App.addListener("resume", () => {
    ensurePushRegistered().catch(() => {});
  });
}

/** 登出時呼叫：換帳號登入後要重新綁定 token */
export function resetPushRegistration() {
  pushState.registered = false;
}

/**
 * 啟用推播並把 token 註冊到後端（綁定目前登入帳號）。
 * 僅能在 Android/iOS App 內執行；瀏覽器會丟出提示錯誤。
 * @param {(token:string)=>void} onToken 取得/更新 token 時回呼
 * @param {(notification:any)=>void} onNotification 收到或點擊通知時回呼
 */
export async function initPush(onToken, onNotification) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("目前不在 App（原生）環境，推播僅能在手機 App 內測試。");
  }

  // 權限
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PushNotifications.requestPermissions();
  }
  pushState.permission = perm.receive;
  if (perm.receive !== "granted") {
    throw new Error("未取得通知權限，請到系統設定開啟通知。");
  }

  // 建立通知頻道（Android 8+ 需要；importance 5 = 會跳 heads-up）
  try {
    await LocalNotifications.createChannel({
      id: PUSH_CHANNEL_ID,
      name: "一般通知",
      importance: 5,
      visibility: 1,
    });
  } catch (e) {
    // 部分平台不支援 createChannel，忽略
  }

  // 綁定監聽（只綁一次）
  if (!listenersBound) {
    listenersBound = true;

    PushNotifications.addListener("registration", async (token) => {
      // 登出時要拿它去解除綁定（unregisterPush）。這是裝置的，不是帳號的，登出不清
      try {
        localStorage.setItem(PUSH_TOKEN_KEY, token.value);
      } catch (e) {
        /* 略過 */
      }
      try {
        await DeviceToken_Register({ token: token.value, platform: "Android", appVersion: "1.0.0" });
        pushState.registered = true;
      } catch (e) {
        console.error("[push] 註冊 token 到後端失敗", e);
      }
      if (onToken) onToken(token.value);
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.error("[push] registration error", err);
    });

    // App 在前景時收到推播 → 用本地通知補跳系統通知（前景才需要），並刷新未讀數
    PushNotifications.addListener("pushNotificationReceived", (n) => {
      showForegroundNotification(n);
      syncNotificationState(n && n.data, false);
      if (onNotification) onNotification(n);
    });

    // 使用者點擊通知 → 標記已讀、刷新未讀數、依 data.route 導頁
    PushNotifications.addListener("pushNotificationActionPerformed", async (a) => {
      const data = (a && a.notification && a.notification.data) || {};
      const marking = syncNotificationState(data, true);
      // 社群 / AI 的推播是絕對網址，整頁跳走會中斷「已讀」請求 → 先等它送完
      const route = resolveNotificationRoute(data.route, data.sourceSite);
      if (leavesPage(route)) await waitAtMost(marking);
      navigateToRoute(route);
      if (onNotification) onNotification(a.notification);
    });
  }

  await PushNotifications.register();
}

/**
 * 登出前呼叫：這支手機不再收目前帳號的推播。
 *
 * 以前登出沒解除，登出後手機還會一直收到那個帳號的推播，直到有人再登入（登入會把 token 綁到新帳號）。
 * 要在登出「之前」呼叫：解除綁定要帶著目前的登入身分。最多等 3 秒，網路慢也不擋住登出。
 */
export async function unregisterPush() {
  if (!Capacitor.isNativePlatform()) return;
  let token = null;
  try {
    token = localStorage.getItem(PUSH_TOKEN_KEY);
  } catch (e) {
    /* 略過 */
  }
  resetPushRegistration();
  if (!token) return;
  try {
    await Promise.race([DeviceToken_Unregister(token), new Promise((resolve) => setTimeout(resolve, 3000))]);
  } catch (e) {
    console.error("[push] 註銷 token 失敗", e);
  }
}
